import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { router } from "@inertiajs/react"

import { csrfToken } from "@/lib/orderActions"

// Shared optimistic-send engine for the three customer chat surfaces (Messages
// inbox, order Chat tab, Team workspace). A sent message renders immediately
// from a client-side pending queue, reconciles with the server copy on the next
// partial reload (no duplicate, no flicker), and — if the send fails afterward —
// shows a small inline error with retry under that bubble.
//
// The reload STAYS by design: these surfaces have no live push, so the reload is
// the only vector for other participants' messages, unread counts, list previews
// and day dividers. The optimistic bubble hides its latency.
//
// Reconciliation is a pure render-time filter: the controller returns the DB
// `messageId` on success, we stamp it on the queue entry as `confirmedId`, and an
// entry is dropped once that id appears in the server thread. The `client_id` we
// POST also dedupes server-side, so a lost-response retry never double-sends.

const FLAGGED_TEXT =
  "This message can't be sent because it includes contact details like a phone number or email. Edit it and send again."
const BLOCKED_TEXT =
  "Direct messaging isn't available on your account right now. Contact support if you think this is a mistake."
const NOT_A_MEMBER_TEXT =
  "You're not in this conversation, so this message can't be sent. Contact support if you think that's wrong."
const GENERIC_TEXT = "This message didn't send."

// Map a failed response to the queue entry's error shape. flagged_content,
// direct_message_blocked and not_a_member are permanent (no Retry: the server
// seats the order's customer itself and reports a seat that fails on Twilio as
// twilio_error, so not_a_member only ever means no seat by right — UPDA-100);
// everything else (twilio_error, network, 5xx, non-json) is transient and
// retryable.
export function mapError(errorType) {
  if (errorType === "flagged_content") {
    return { errorType: "flagged_content", canRetry: false, errorText: FLAGGED_TEXT }
  }
  if (errorType === "direct_message_blocked") {
    return { errorType: "direct_message_blocked", canRetry: false, errorText: BLOCKED_TEXT }
  }
  if (errorType === "not_a_member") {
    return { errorType: "not_a_member", canRetry: false, errorText: NOT_A_MEMBER_TEXT }
  }
  return { errorType: errorType || null, canRetry: true, errorText: GENERIC_TEXT }
}

function newClientId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID()
  return `c-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

// PR-012 — the transport representation is built ONLY here, at the request
// boundary. The plain `@DisplayName` body the user sees stays untouched; each
// recorded mention is wrapped in the `.mention user-<id>` span the two server
// parsers read. One pass over an alternation of the mention tokens (longest name
// first) so a wrapped span is never re-scanned. Entries with no mention map are
// returned unchanged.
function serializeMentions(body, mentions) {
  if (!mentions) return body

  const entries = Object.entries(mentions)
  if (entries.length === 0) return body

  const sorted = entries.sort((a, b) => b[0].length - a[0].length)
  const byToken = new Map(sorted.map(([name, id]) => [`@${name}`, id]))
  const alternation = sorted.map(([name]) => escapeRegExp(`@${name}`)).join("|")
  const pattern = new RegExp(alternation, "g")

  return body.replace(pattern, (match) => {
    const id = byToken.get(match)
    if (id == null) return match

    return `<span class="mention user-${id}">${match}</span>`
  })
}

export default function useOptimisticSend({ sid, only, serverItems = [] }) {
  const [queue, setQueue] = useState([])

  // Refs so the callbacks stay stable while always reading the current props.
  const sidRef = useRef(sid)
  sidRef.current = sid
  const onlyRef = useRef(only)
  onlyRef.current = only
  const queueRef = useRef(queue)
  queueRef.current = queue

  // The set of DB ids the server thread already carries — the reconciliation key.
  const serverIds = useMemo(
    () => new Set((serverItems ?? []).map((item) => item?.id).filter((id) => id != null)),
    [serverItems]
  )

  // POST (or re-POST on retry) a single queue entry, then reconcile.
  const postMessage = useCallback(async (entry) => {
    try {
      const res = await fetch(`/conversations/${entry.sid}/conversation_messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ message: serializeMentions(entry.body, entry.mentions), client_id: entry.clientId }),
      })

      let json = null
      try {
        json = await res.json()
      } catch {
        json = null
      }

      if (res.ok) {
        setQueue((q) =>
          q.map((e) =>
            e.clientId === entry.clientId
              ? { ...e, status: "confirmed", confirmedId: json?.messageId ?? null, errorType: null, errorText: null, canRetry: false }
              : e
          )
        )
        router.reload({ only: onlyRef.current })
        return
      }

      const mapped = mapError(json?.errorType)
      setQueue((q) => q.map((e) => (e.clientId === entry.clientId ? { ...e, status: "failed", ...mapped } : e)))
    } catch {
      const mapped = mapError(null)
      setQueue((q) => q.map((e) => (e.clientId === entry.clientId ? { ...e, status: "failed", ...mapped } : e)))
    }
  }, [])

  const send = useCallback((body, { mentions } = {}) => {
    const text = typeof body === "string" ? body.trim() : ""
    if (!text) return

    // The plain-text body and the name→id map are both stored on the entry; the
    // body renders as-is in every bubble and the map is serialized only inside
    // postMessage (PR-012). Callers without mentions pass nothing and serialize
    // unchanged.
    const mentionMap = mentions && Object.keys(mentions).length ? mentions : null

    const activeSid = sidRef.current
    if (!activeSid) {
      // No conversation to send into (e.g. an order whose customer thread does
      // not exist yet — OrderDetailPresenter returns { sid: null }). The composer
      // has already cleared the textarea, so returning silently would drop the
      // text with no feedback — the exact silent loss this hook removes. Surface
      // a failed bubble instead: the body stays visible and Dismiss-able. Retry
      // can't help without a sid, so it is not offered.
      setQueue((q) => [
        ...q,
        {
          clientId: newClientId(),
          sid: activeSid,
          body: text,
          mentions: mentionMap,
          status: "failed",
          errorType: null,
          errorText: GENERIC_TEXT,
          canRetry: false,
          confirmedId: null,
        },
      ])
      return
    }

    const entry = {
      clientId: newClientId(),
      sid: activeSid,
      body: text,
      mentions: mentionMap,
      status: "sending",
      errorType: null,
      errorText: null,
      canRetry: false,
      confirmedId: null,
    }
    setQueue((q) => [...q, entry])
    postMessage(entry)
  }, [postMessage])

  const retry = useCallback((clientId) => {
    const entry = queueRef.current.find((e) => e.clientId === clientId)
    if (!entry) return

    setQueue((q) =>
      q.map((e) =>
        e.clientId === clientId ? { ...e, status: "sending", errorType: null, errorText: null, canRetry: false } : e
      )
    )
    postMessage({ ...entry, status: "sending" })
  }, [postMessage])

  // Remove the entry and hand its plain-text body AND its mention name→id map back
  // so the caller can refill the composer (the flagged-content "Edit" affordance).
  // The map rides along so a resend re-serializes the `.mention user-<id>` span
  // exactly like a fresh send (PR-012); without it the edited resend would post
  // plain text and the mentioned participant would get no notification (CR-009).
  const editAndRemove = useCallback((clientId) => {
    const entry = queueRef.current.find((e) => e.clientId === clientId)
    setQueue((q) => q.filter((e) => e.clientId !== clientId))
    return { body: entry?.body ?? "", mentions: entry?.mentions ?? null }
  }, [])

  const dismiss = useCallback((clientId) => {
    setQueue((q) => q.filter((e) => e.clientId !== clientId))
  }, [])

  // Garbage-collect confirmed entries once the reload lands them in the server
  // thread, so the queue does not grow unbounded across a session.
  useEffect(() => {
    if (serverIds.size === 0) return
    setQueue((q) => {
      const next = q.filter((e) => !(e.confirmedId != null && serverIds.has(e.confirmedId)))
      return next.length === q.length ? q : next
    })
  }, [serverIds])

  // Entries for the active conversation, shaped as MessageThread items and pruned
  // of any whose confirmed id is already in the server thread (belt-and-suspenders
  // with the GC effect: avoids a one-frame duplicate the instant the reload lands).
  const pendingItems = useMemo(
    () =>
      queue
        .filter((e) => e.sid === sid)
        .filter((e) => !(e.confirmedId != null && serverIds.has(e.confirmedId)))
        .map((e) => ({
          kind: "message",
          id: e.clientId,
          mine: true,
          body: e.body,
          pending: e.status !== "failed",
          error: e.status === "failed" ? { type: e.errorType, text: e.errorText, canRetry: e.canRetry } : null,
        })),
    [queue, sid, serverIds]
  )

  const sending = useMemo(() => queue.some((e) => e.sid === sid && e.status === "sending"), [queue, sid])

  return { pendingItems, send, retry, editAndRemove, dismiss, sending }
}
